<template>
  <b-modal
    ref="modal"
    :title="$t('influencer_panel.info_title')"
    :ok-only="true"
    :ok-title="$t('action.close')"
    ok-variant="outline-secondary"
    size="lg"
    @hidden="reset"
  >
    <template v-if="current">
      <p class="info__campaign">{{ campaignName(current) }}</p>

      <div v-if="infoRows.length" class="info__grid">
        <div v-for="row in infoRows" :key="row.label" class="info__row">
          <span class="info__key">{{ row.label }}</span>
          <span class="info__value">{{ row.value }}</span>
        </div>
      </div>

      <div v-if="campaign.objective" class="info__block">
        <div class="info__label">{{ $t('influencer_panel.info_objective') }}</div>
        <p class="info__text">{{ campaign.objective }}</p>
      </div>

      <div v-if="campaign.rules" class="info__block">
        <div class="info__label">{{ $t('influencer_panel.rules') }}</div>
        <p class="info__text">{{ campaign.rules }}</p>
      </div>

      <div v-if="campaign.dos" class="info__block">
        <div class="info__label">{{ $t('influencer_panel.info_dos') }}</div>
        <p class="info__text">{{ campaign.dos }}</p>
      </div>

      <div v-if="campaign.donts" class="info__block">
        <div class="info__label">{{ $t('influencer_panel.info_donts') }}</div>
        <p class="info__text">{{ campaign.donts }}</p>
      </div>

      <div v-if="mentions.length" class="info__block">
        <div class="info__label">{{ $t('influencer_panel.info_mentions') }}</div>
        <div class="info__chips">
          <b-badge v-for="mention in mentions" :key="mention" variant="light-primary">@{{ mention }}</b-badge>
        </div>
      </div>

      <div v-if="campaign.disclosure_tag" class="info__block">
        <div class="info__label">{{ $t('influencer_panel.disclosure_tag') }}</div>
        <b-badge variant="light-warning">{{ campaign.disclosure_tag }}</b-badge>
      </div>
    </template>
  </b-modal>
</template>

<script>
import { BModal, BBadge } from 'bootstrap-vue'
import { currencyFormatter } from '@/util/Util'
import { campaignName, commissionLabel, discountLabel } from '../campaign'
import { shortDate } from '../panel'

/**
 * Informacoes e regras da campanha, na visao do influencer.
 *
 * Nao busca nada: todos os campos — comerciais (desconto, comissao, carencia,
 * janela) e de conteudo (regras, faca/evite, mencoes, tag) — ja viajam na
 * participacao devolvida por `/me/campaigns`. A modal de material continua
 * separada porque as pecas vem de outro endpoint e sao pesadas.
 *
 * A participacao chega por argumento no open() (nao so pela prop) porque o pai
 * seta a prop e abre no mesmo tick — a prop ainda nao propagou nesse momento.
 */
const NETWORK_LABELS = {
  IG: 'Instagram',
  TT: 'TikTok',
  YT: 'YouTube',
  WA: 'WhatsApp',
  FB: 'Facebook',
  BI: 'Link na bio',
  OT: 'Outro',
}

export default {
  name: 'CampaignInfoModal',
  components: { BModal, BBadge },
  props: {
    participation: { type: Object, default: null },
  },
  data: () => ({
    target: null,
  }),
  computed: {
    current() {
      return this.target || this.participation
    },
    campaign() {
      return (this.current && this.current.campaign) || {}
    },
    mentions() {
      const raw = this.campaign.mentions || []
      const list = Array.isArray(raw) ? raw : String(raw).split(/[\s,]+/)

      return list.filter(Boolean).map(mention => String(mention).replace(/^@/, ''))
    },
    networks() {
      const raw = this.campaign.required_networks || []
      const list = Array.isArray(raw) ? raw : String(raw).split(/[\s,]+/)

      return list.filter(Boolean).map(code => NETWORK_LABELS[code] || code)
    },
    infoRows() {
      const c = this.campaign
      const rows = []
      const money = value => currencyFormatter(Number(value))

      if (c.starts_at) {
        rows.push({
          label: this.$t('influencer_panel.info_period'),
          value: c.ends_at
            ? `${shortDate(c.starts_at)} a ${shortDate(c.ends_at)}`
            : this.$t('influencer_panel.info_period_open', { date: shortDate(c.starts_at) }),
        })
      }

      const discount = discountLabel(c)
      if (discount) {
        const parts = [discount]
        if (c.max_discount_value !== null && c.max_discount_value !== undefined) {
          parts.push(this.$t('influencer_panel.info_discount_cap', { value: money(c.max_discount_value) }))
        }
        if (c.min_order_value !== null && c.min_order_value !== undefined) {
          parts.push(this.$t('influencer_panel.info_min_order', { value: money(c.min_order_value) }))
        }
        if (c.first_purchase_only) parts.push(this.$t('influencer_panel.info_first_purchase'))
        if (c.usage_limit_total) parts.push(this.$t('influencer_panel.info_usage_total', { count: c.usage_limit_total }))
        if (c.usage_limit_per_customer) {
          parts.push(this.$t('influencer_panel.info_usage_per_customer', { count: c.usage_limit_per_customer }))
        }
        rows.push({ label: this.$t('influencer_panel.info_buyer_discount'), value: parts.join(' · ') })
      }

      const commission = commissionLabel(this.current)
      if (commission !== '—') {
        const base = c.commission_base === 'GR'
          ? this.$t('influencer_panel.info_commission_base_GR')
          : this.$t('influencer_panel.info_commission_base_LQ')
        rows.push({ label: this.$t('influencer_panel.info_your_commission'), value: `${commission} ${base}` })
      }

      if (c.influencer_free_uses > 0) {
        rows.push({
          label: this.$t('influencer_panel.info_free_uses'),
          value: this.$t('influencer_panel.info_free_uses_value', { count: c.influencer_free_uses }),
        })
      }

      if (c.hold_days !== null && c.hold_days !== undefined) {
        const key = c.hold_from === 'PY' ? 'influencer_panel.info_hold_PY' : 'influencer_panel.info_hold_DL'
        rows.push({ label: this.$t('influencer_panel.info_hold'), value: this.$t(key, { days: c.hold_days }) })
      }

      if (c.attribution_window_days) {
        rows.push({
          label: this.$t('influencer_panel.info_attribution'),
          value: this.$t('influencer_panel.info_attribution_days', { days: c.attribution_window_days }),
        })
      }

      const content = []
      if (c.requires_content) content.push(this.$t('influencer_panel.info_content_proof'))
      if (c.min_posts) content.push(this.$t('influencer_panel.info_min_posts', { count: c.min_posts }))
      if (c.exclusivity_days) content.push(this.$t('influencer_panel.info_exclusivity', { days: c.exclusivity_days }))
      if (content.length) {
        rows.push({ label: this.$t('influencer_panel.info_content'), value: content.join(' · ') })
      }

      if (this.networks.length) {
        rows.push({ label: this.$t('influencer_panel.info_networks'), value: this.networks.join(', ') })
      }

      return rows
    },
  },
  methods: {
    campaignName,
    open(participation = null) {
      this.target = participation || this.participation
      this.$refs.modal.show()
    },
    reset() {
      this.target = null
    },
  },
}
</script>

<style scoped>
.info__campaign {
  margin: 0 0 1rem;
  font-weight: var(--fw-medium);
  color: var(--text-heading);
}

.info__grid {
  display: grid;
  gap: 0.45rem;
  margin-bottom: 1.25rem;
  padding: 0.85rem 1rem;
  background: var(--surface-sunken);
  border-radius: var(--radius);
}

.info__row {
  display: flex;
  flex-wrap: wrap;
  gap: 0.25rem 0.75rem;
  font-size: var(--fs-sm);
}

.info__key {
  flex: 0 0 170px;
  color: var(--text-muted);
}

.info__value {
  flex: 1;
  min-width: 200px;
  color: var(--text-body);
}

.info__block {
  margin-bottom: 1.1rem;
}

.info__label {
  font-size: var(--fs-sm);
  font-weight: var(--fw-medium);
  color: var(--text-heading);
  margin-bottom: 0.35rem;
}

.info__text {
  margin: 0;
  font-size: var(--fs-sm);
  color: var(--text-body);
  white-space: pre-line;
}

.info__chips {
  display: flex;
  flex-wrap: wrap;
  gap: 0.35rem;
}
</style>
